import { AdvanceQualificationMethod } from '@varo/graphql-schema-types';
import { Redirect, Stack, useRouter } from 'expo-router';
import {
  isNotQualified,
  isQualified,
} from 'features/advance/helpers/AdvanceProducts.helpers';
import useVaroAdvanceProducts from 'features/advance/hooks/useGetVaroAdvanceProducts';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';

import DDApprovedCongrats from './components/DDApprovedCongrats/DDApprovedCongrats';
import VaD0EligibleApprovedCongrats from './components/VaD0EligibleApprovedCongrats/VaD0EligibleApprovedCongrats';

const AdvanceApprovedCongratsScreen = () => {
  const router = useRouter();
  const { t: translatedErrorText } = useTranslation('advance', {
    keyPrefix: 'error',
  });
  const { isFeatureEnabled } = useStatsig();

  const isLightningJourneyCongratsEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_LIGHTNING_JOURNEY_CONGRATS,
  );

  const { data, fetching, error } = useVaroAdvanceProducts(
    'network-only',
    false,
    true,
  );

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
    HomeTileTypeIds.ADVANCE_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BORROW,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.NEXT_BEST_ACTION_TILE,
  ]);

  const {
    isLoading: pinwheelFlowLoading,
    pinwheelFlowPageError,
    shouldRedirectToDDManagementPage,
  } = useDirectDepositManagementFlow();

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  useEffect(() => {
    if (isNotQualified(data)) {
      // shouldn't happen as we have checked for qualification on employment screen
      <Redirect href="/varo-advance/qualification" />;
    }
  }, [data]);

  const qualifiedData = isQualified(data) ? data : null;
  const options = qualifiedData?.options ?? [];
  const hasOptions = options.length > 0;

  const isVaD0Eligible =
    hasOptions &&
    options.some(
      (option) =>
        option.qualificationMethod ===
        AdvanceQualificationMethod.ExternalAccount,
    );

  const showVADay0CongratsScreen =
    isVaD0Eligible &&
    !shouldRedirectToDDManagementPage &&
    isLightningJourneyCongratsEnabled;

  const maxApprovedAmount = isQualified(data) ? data.options[0].title : '';

  useViewDidFocusScreenAnalyticsEvent({
    key: '/varo-advance/approved',
    customAttributes: {
      source_name: '',
      category: 'varo_advance',
      subcategory: showVADay0CongratsScreen ? 'va_day_0' : 'va_application',
    },
  });

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollView}>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ScreenTransitionOverlay
          isLoading={fetching || pinwheelFlowLoading}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            error || pinwheelFlowPageError ? (
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={translatedErrorText('error.title')}
                messageBody={translatedErrorText('error.body')}
                primaryButton={{
                  title: translatedErrorText('error.button'),
                  onPress: () => router.navigate('/'),
                }}
              />
            ) : null
          }
        />

        {showVADay0CongratsScreen ? (
          <VaD0EligibleApprovedCongrats maxApprovedAmount={maxApprovedAmount} />
        ) : (
          <DDApprovedCongrats maxApprovedAmount={maxApprovedAmount} />
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
});

export default AdvanceApprovedCongratsScreen;
